import './time-picker.scss';

import { Col, Empty, Row } from 'antd';

import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Heading, { HeadingSize } from '../../components/heading/heading';
import SessionTime from '../../interfaces/ISessionTime';
import pluralHelper from '../../helpers/plural';
import { actionCreators as SessionActions } from '../../store/Sessions';
import { actionCreators as BookingActions, BookingStatus } from '../../store/Booking';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import TimeButton from './time-button';
import { IApplicationState } from '../../store';
import { ITicketPriceRange } from '../../interfaces/ITicketPriceRange';
import SessionTimeDisplayItem from '../../interfaces/ISessionTimeDisplayItem';
import PriceBucketLabels from '../priceBucketLabels/PriceBucketLabels';
import { PurchaseTypes } from '../../store/PurchaseTypes';

export interface ITimePickerProps {
    loading: boolean;
    dayTimes?: SessionTime[]; //presorted :)
    eveningTimes?: SessionTime[]; //presorted :)
    selectedTime?: SessionTime;
    locale: string;
    players?: number;
}

export interface ITimeButtonWrapperProps {
    time: SessionTime;
    selected: SessionTime | undefined;
    players: number | undefined;
    locale: string;
    highlightTime: boolean;
}

//parse the status display text to be shown on the time buttons
function getWarningStatusText(
    t: (key: string, options?: any) => string,
    locale: string,
    item: SessionTimeDisplayItem
): string {
    if (!item.disabled) return '';

    if (item.noSlotsLeft) {
        return t('statuses.soldOut');
    }

    if (item.tooManyPlayers) {
        const key = pluralHelper.getKey(locale, 'statuses.spotsLeft', item.slotsRemaining);
        return t(key, { count: item.slotsRemaining });
    }

    if (item.slotsRemaining === item.openTimeMinimumPlayers) {
        return t('statuses.players_requirement', { count: item.slotsRemaining });
    }

    return t('statuses.players_range_requirement', {
        minimum: item.openTimeMinimumPlayers,
        maximum: item.slotsRemaining,
    });
}

//convert a session time item into a display item which has all the logic applied about how this time compares to the users filter requirements
function getSessionTimeDisplayItem(
    props: ITimePickerProps,
    t: (key: string, options?: any) => string,
    x: SessionTime
): SessionTimeDisplayItem {
    const isSelected = props.selectedTime !== undefined && props.selectedTime.time === x.time;
    const notEnoughPlayers = props.players !== undefined && x.openTimeMinimumPlayers > props.players;
    const tooManyPlayers = props.players !== undefined && x.slotsRemaining < props.players;
    const noSlotsLeft = x.slotsRemaining === 0;

    const disabled = noSlotsLeft || tooManyPlayers || notEnoughPlayers;


    let item = {
        time: x.time,
        slotsRemaining: x.slotsRemaining,
        openTimeMinimumPlayers: x.openTimeMinimumPlayers,
        slotMinPrice: x.slotMinPrice,
        slotMaxPrice: x.slotMaxPrice,
        isSelected: isSelected,
        notEnoughPlayers: notEnoughPlayers,
        tooManyPlayers: tooManyPlayers,
        noSlotsLeft: noSlotsLeft,
        disabled: disabled
    } as SessionTimeDisplayItem

    const warningStatusText = getWarningStatusText(t, props.locale, item);
    item.warningStatusText = warningStatusText;

    return item;
}

const TimePicker: React.FunctionComponent<ITimePickerProps> = (props:ITimePickerProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const selectedPurchaseType = useSelector((state: IApplicationState) => state.purchaseType.selectedPurchaseType);
    const highlightPriceEnabled = useSelector((state: IApplicationState) => state.sites.bookingOptions?.usePriceTierHighlighting) ?? false;

    const priceBucketsEnabled = useMemo(() => {
        return highlightPriceEnabled && selectedPurchaseType === PurchaseTypes.Booking
    }, [highlightPriceEnabled, selectedPurchaseType]);

    const pendingBooking = useSelector((state: IApplicationState) => state.booking.booking);
    const bookingStatus = useSelector((state: IApplicationState) => state.booking.status);

    //first convert the raw session times list into an object that has the meta about how to display it
    const displayableDayTimes: SessionTimeDisplayItem[] = React.useMemo(() => {
        return props?.dayTimes?.map((x) => {
            return getSessionTimeDisplayItem(props, t, x);
        }) ?? [];
    }, [props, t]);
    const displayableEveningTimes: SessionTimeDisplayItem[] = React.useMemo(() => {
        return props?.eveningTimes?.map((x) => {
            return getSessionTimeDisplayItem(props, t, x);
        }) ?? [];
    }, [props, t]);

    const ticketPriceRange: ITicketPriceRange = React.useMemo(() => {
        if (!priceBucketsEnabled) {
            return { min: 0, max: 0, range20: 0, highlightThreshold: 0, rangeIsValid: false } as ITicketPriceRange;
        }

        const allTimes: SessionTimeDisplayItem[] = [
            ...(displayableDayTimes ?? []),
            ...(displayableEveningTimes ?? []),
        ];

        const allValidTimes = allTimes.filter(x => !x.disabled && x.slotMinPrice > 0 && x.slotMaxPrice > 0);

        if (allValidTimes.length === 0) {
            return { min: 0, max: 0, range20: 0, highlightThreshold: 0, rangeIsValid:false } as ITicketPriceRange;
        }

        const minPrices = allValidTimes
            .map((d) => d.slotMinPrice)
            .filter((v) => typeof v === 'number' && !isNaN(v));

        if (minPrices.length === 0 ) {
            return { min: 0, max: 0, range20: 0, highlightThreshold: 0, rangeIsValid: false } as ITicketPriceRange;
        }

        //only compare the highest and lowest of the min prices
        const min = Math.min(...minPrices);
        const max = Math.max(...minPrices);

        const range20 = min === max ? 0 : (max - min) * 0.2;
        const highlightThreshold = min + range20;

        const isValid = min !== max && highlightThreshold > 0;

        const res = { min, max, range20, highlightThreshold, rangeIsValid: isValid } as ITicketPriceRange;
        return res;
    }, [displayableDayTimes, displayableEveningTimes, priceBucketsEnabled ]);

    const timeClicked = useCallback(
        (time: SessionTime) => {
            if (pendingBooking && bookingStatus === BookingStatus.Pending) {
                dispatch(BookingActions.expireBooking());
            }

            if (time) dispatch(SessionActions.postTimeSelectedAction(time));
        },
        [bookingStatus, dispatch, pendingBooking]
    );

    const NoDataState: React.FunctionComponent = () => {
        const emptyLogo = `/static/zl-black-new.png`;
        return (
            <Empty
                description={t('sessionDatePicker.labels.noDatesAvailable')}
                image={emptyLogo}
                imageStyle={{ opacity: 0.1, height: 50, margin: 20 }}
            />
        );
    };

    if (props.loading) {
        return <LoadingSpinner />;
    }
    if (props.dayTimes === undefined && props.eveningTimes === undefined) {
        return <NoDataState />;
    }
    if (props.dayTimes?.length === 0 && props.eveningTimes?.length === 0) {
        return <NoDataState />;
    }

    const dayValues: React.ReactElement =
        displayableDayTimes !== undefined && displayableDayTimes.length > 0 ? (
            <React.Fragment>
                <Row justify="start" align="top">
                    <Col span={24}>
                        <Heading level={HeadingSize.Four} className="time-heading">
                            {t('sessionTimePicker.titles.daySession')}
                        </Heading>
                    </Col>
                </Row>
                <Row justify="space-around" align="top" className="zl-time-picker-container day">
                    <Col span={24}>
                        {displayableDayTimes.map((x: SessionTimeDisplayItem) => (
                            <TimeButton
                                key={x.time}
                                item={x}
                                selected={x.isSelected}
                                disabled={x.disabled}
                                soldOut={x.noSlotsLeft}
                                warningText={x.warningStatusText}
                                onClick={timeClicked}
                                highlightTime={priceBucketsEnabled && !x.disabled && (ticketPriceRange.rangeIsValid && x.slotMinPrice < ticketPriceRange.highlightThreshold)}
                                priceRangeValid={priceBucketsEnabled && ticketPriceRange.rangeIsValid}
                            />
                        ))}
                    </Col>
                </Row>
            </React.Fragment>
        ) : (
            <React.Fragment />
        );

    const eveningValues: React.ReactElement =
        displayableEveningTimes !== undefined && displayableEveningTimes.length > 0 ? (
            <React.Fragment>
                <Row justify="start" align="top">
                    <Col span={24}>
                        <Heading level={HeadingSize.Four} className="time-heading">
                            {t('sessionTimePicker.titles.eveningSession')}
                        </Heading>
                    </Col>
                </Row>
                <Row justify="space-around" align="top" className="zl-time-picker-container night">
                    <Col span={24}>
                        {displayableEveningTimes.map((x: SessionTimeDisplayItem) => (
                            <TimeButton
                                key={x.time}
                                item={x}
                                selected={x.isSelected}
                                disabled={x.disabled}
                                soldOut={x.noSlotsLeft}
                                warningText={x.warningStatusText}
                                onClick={timeClicked}
                                highlightTime={priceBucketsEnabled && !x.disabled && (ticketPriceRange.rangeIsValid && x.slotMinPrice < ticketPriceRange.highlightThreshold)}
                                priceRangeValid={priceBucketsEnabled && ticketPriceRange.rangeIsValid}
                            />
                        ))}
                    </Col>
                </Row>
            </React.Fragment>
        ) : (
            <React.Fragment />
        );

    return (
        <React.Fragment>
            {dayValues}
            {eveningValues}
            {priceBucketsEnabled && ticketPriceRange.rangeIsValid && <PriceBucketLabels />}
        </React.Fragment>
    );
};

export default React.memo(TimePicker);
